import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { LiveTag, TeamAvatar, fmtTime, fmtDateTime, isFavorite, toggleFavorite, ThemeToggle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Heart, Home, Radio, Calendar, Users, GitBranch, Trophy, MapPin, Megaphone, Lock } from "lucide-react";

const NAV = [
  { id: "home", label: "Home", icon: Home },
  { id: "live", label: "Live", icon: Radio },
  { id: "schedule", label: "Schedule", icon: Calendar },
  { id: "teams", label: "Teams", icon: Users },
  { id: "tournament", label: "Bracket", icon: GitBranch },
  { id: "results", label: "Results", icon: Trophy },
];

export default function PublicTournament() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("home");
  const [fav, setFav] = useState(isFavorite(code));
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/${code}`).then((r) => setD(r.data)).catch(() => setNotFound(true));
  }, [code]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [load]);

  if (notFound) return <Center>Tournament not found.</Center>;
  if (!d) return <Center>Loading…</Center>;

  const t = d.tournament;
  const primary = "hsl(var(--primary))";
  const closed = ["completed", "archived"].includes(t.status);
  const isDraft = t.status === "draft";

  const onFav = () => { toggleFavorite(code); setFav(!fav); toast.success(fav ? "Removed from favorites" : "Added to favorites"); };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="relative">
        <div className="h-36 w-full bg-muted">
          {t.logo_url && <img src={fileUrl(t.logo_url)} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" />}
        </div>
        <div className="absolute left-3 top-3">
          <Button asChild size="sm" variant="secondary" className="bg-background/80 backdrop-blur">
            <Link to="/" data-testid="back-to-main-site">
              <ArrowLeft className="h-4 w-4" /> Main website
            </Link>
          </Button>
        </div>
        <div className="absolute right-3 top-3"><ThemeToggle /></div>
        <div className="relative z-10 mx-auto -mt-12 max-w-lg px-4">
          <div className="flex items-end gap-3">
            <div className="rounded-2xl border-4 border-background bg-card p-1 shadow-lg">
              {t.logo_url ? <img src={fileUrl(t.logo_url)} alt="" className="h-16 w-16 rounded-xl object-cover" />
                : <TeamAvatar team={{ name: t.name }} size={64} />}
            </div>
            <Button size="sm" variant={fav ? "default" : "outline"} className="mb-1" onClick={onFav} data-testid="favorite-button"
              style={fav ? { backgroundColor: primary } : {}}>
              <Heart className="mr-1 h-4 w-4" fill={fav ? "currentColor" : "none"} /> {fav ? "Favorited" : "Favorite"}
            </Button>
          </div>
          <h1 className={`mt-3 font-heading text-2xl font-black tracking-tight ${closed ? "strike-through-name" : ""}`}>{t.name}</h1>
          <p className="text-sm text-muted-foreground">{t.location || "—"}</p>
        </div>
      </div>

      <div className="mx-auto mt-6 max-w-lg px-4">
        {tab === "home" && <HomeView d={d} primary={primary} setTab={setTab} />}
        {tab === "live" && (isDraft ? <DraftNotice /> : <LiveView d={d} />)}
        {tab === "schedule" && (isDraft ? <DraftNotice /> : <ScheduleView d={d} />)}
        {tab === "teams" && (isDraft ? <DraftNotice /> : <TeamsView d={d} />)}
        {tab === "tournament" && (isDraft ? <DraftNotice /> : <BracketOrStandings d={d} />)}
        {tab === "results" && <ResultsView d={d} />}
      </div>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg justify-between px-2 py-1.5">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} data-testid={`public-tab-${n.id}`}
              className="flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1.5 text-[10px] font-medium"
              style={{ color: tab === n.id ? primary : undefined }}>
              <n.icon className="h-5 w-5" style={{ color: tab === n.id ? primary : "var(--muted-foreground)" }} />
              {n.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

const Center = ({ children }) => <div className="flex h-screen items-center justify-center text-muted-foreground">{children}</div>;
const DraftNotice = () => <Empty>This tournament is still in draft.</Empty>;

function sponsorUrl(url) {
  if (!url) return null;
  const withProtocol = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  try {
    const parsed = new URL(withProtocol);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

function SponsorLink({ sponsor, children }) {
  const href = sponsorUrl(sponsor.url);
  const className = "flex items-center gap-2 rounded-xl border bg-card px-3 py-2 transition-colors hover:bg-accent";
  if (!href) return <div className={className}>{children}</div>;
  return <a href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid={`sponsor-link-${sponsor.id}`}>{children}</a>;
}

function HomeView({ d, primary, setTab }) {
  const t = d.tournament;
  const isDraft = t.status === "draft";
  const live = d.matches.filter((m) => m.status === "live");
  const next = d.matches.filter((m) => m.status === "pending" && m.team_a && m.team_b)
    .sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || "")).slice(0, 3);
  return (
    <div className="space-y-5">
      <div>
        <SectionH>Tournament information</SectionH>
        <div className="space-y-3">
          {t.description && <Card className="p-4 text-sm text-muted-foreground">{t.description}</Card>}
          <div className="grid grid-cols-3 gap-2">
            {[["Teams", d.teams.length], ["Matches", d.matches.length], ["Fields", d.fields.length]].map(([l, v]) => (
              <Card key={l} className="p-3 text-center"><div className="font-heading text-2xl font-black" style={{ color: primary }}>{v}</div><div className="text-label text-muted-foreground">{l}</div></Card>
            ))}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Calendar className="h-4 w-4" /> {t.date || "TBD"} · <MapPin className="h-4 w-4" /> {t.location || "—"}</div>
        </div>
      </div>

      {d.announcements?.length > 0 && (
        <div><SectionH>Announcements</SectionH>
          {d.announcements.map((a) => (
            <Card key={a.id} className="mb-2 flex gap-2 p-3"><Megaphone className="mt-0.5 h-4 w-4" style={{ color: primary }} /><div><div className="font-semibold">{a.title}</div><div className="text-sm text-muted-foreground">{a.body}</div></div></Card>
          ))}
        </div>
      )}
      {d.sponsors?.length > 0 && (
        <div><SectionH>Sponsors</SectionH>
          <div className="flex flex-wrap gap-2">
            {d.sponsors.map((s) => (
              <SponsorLink key={s.id} sponsor={s}>
                {s.logo_url ? <img src={fileUrl(s.logo_url)} alt="" className="h-6 w-6 rounded object-cover" /> : <div className="flex h-6 w-6 items-center justify-center rounded bg-muted text-[10px] font-bold">{s.name[0]}</div>}
                <span className="text-sm font-medium">{s.name}</span>
              </SponsorLink>
            ))}
          </div>
        </div>
      )}
      {isDraft ? <DraftNotice /> : next.length > 0 && <div><SectionH>Up next</SectionH>{next.map((m) => <PublicMatch key={m.id} m={m} />)}</div>}
      {!isDraft && live.length > 0 && <div><SectionH>Live now</SectionH>{live.map((m) => <PublicMatch key={m.id} m={m} />)}</div>}
    </div>
  );
}

function LiveView({ d }) {
  const live = d.matches.filter((m) => m.status === "live");
  return <div>{live.length === 0 ? <Empty>No matches are live right now.</Empty> : live.map((m) => <PublicMatch key={m.id} m={m} />)}</div>;
}

function ScheduleView({ d }) {
  const sorted = [...d.matches].sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
  return <div>{sorted.length === 0 ? <Empty>Schedule not published yet.</Empty> : sorted.map((m) => <PublicMatch key={m.id} m={m} showTime />)}</div>;
}

function ResultsView({ d }) {
  const finals = d.matches.filter((m) => m.status === "final");
  return <div>{finals.length === 0 ? <Empty>No results yet.</Empty> : finals.map((m) => <PublicMatch key={m.id} m={m} />)}</div>;
}

function TeamsView({ d }) {
  return (
    <div className="grid gap-2">
      {d.teams.map((tm) => (
        <Card key={tm.id} className="p-3" data-testid={`public-team-${tm.id}`}>
          <div className="flex items-center gap-3">
            <TeamAvatar team={tm} />
            <div className="flex-1"><div className="font-semibold">{tm.name}</div><div className="text-xs text-muted-foreground">{tm.captain_name ? `Captain: ${tm.captain_name}` : ""} · {tm.members.length} members</div></div>
          </div>
          {tm.members.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{tm.members.map((mem, i) => <span key={i} className="rounded-full bg-muted px-2 py-0.5 text-xs">{mem.name}</span>)}</div>}
        </Card>
      ))}
    </div>
  );
}

function BracketOrStandings({ d }) {
  if (d.tournament.tournament_type === "round_robin") {
    return (
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground"><tr><th className="p-2 text-left">Team</th><th className="p-2">P</th><th className="p-2">W</th><th className="p-2">D</th><th className="p-2">L</th><th className="p-2">Pts</th></tr></thead>
          <tbody>{(d.standings || []).map((s, i) => (
            <tr key={s.team_id} className="border-t"><td className="flex items-center gap-2 p-2">{i + 1}. <TeamAvatar team={{ name: s.name, color: s.color }} size={20} />{s.name}</td><td className="p-2 text-center">{s.played}</td><td className="p-2 text-center">{s.won}</td><td className="p-2 text-center">{s.drawn}</td><td className="p-2 text-center">{s.lost}</td><td className="p-2 text-center font-bold">{s.points}</td></tr>
          ))}</tbody>
        </table>
      </Card>
    );
  }
  const isGroup = d.tournament.tournament_type === "group_elimination";
  const bracketMatches = isGroup ? d.matches.filter((m) => m.bracket === "main") : d.matches;
  const rounds = [...new Set(bracketMatches.map((m) => m.round))].sort((a, b) => a - b);
  const qual = (d.tournament.type_config || {}).qualifiers || 2;
  return (
    <div className="space-y-6">
      {isGroup && (
        <div className="space-y-3">
          {Object.entries(d.group_standings || {}).map(([label, rows]) => (
            <Card key={label} className="overflow-hidden" data-testid={`public-group-${label}`}>
              <div className="bg-muted/50 px-3 py-2 text-label text-muted-foreground">Group {label}</div>
              <table className="w-full text-sm">
                <tbody>{rows.map((s, i) => (
                  <tr key={s.team_id} className={`border-t ${i < qual ? "bg-primary/5" : ""}`}>
                    <td className="flex items-center gap-2 p-2">{i + 1}. <TeamAvatar team={{ name: s.name, color: s.color }} size={18} /><span className="truncate">{s.name}</span></td>
                    <td className="p-2 text-center text-xs text-muted-foreground">{s.won}-{s.drawn}-{s.lost}</td>
                    <td className="p-2 text-center font-bold">{s.points}</td>
                  </tr>
                ))}</tbody>
              </table>
            </Card>
          ))}
        </div>
      )}
      {isGroup && <div className="text-label text-muted-foreground">Elimination stage</div>}
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="public-bracket">
        {rounds.map((r) => {
          const rm = bracketMatches.filter((m) => m.round === r).sort((a, b) => a.slot - b.slot);
          return (
            <div key={r} className="flex min-w-[200px] flex-col justify-around gap-3">
              <div className="text-label text-center text-muted-foreground">{rm[0]?.round_name}</div>
              {rm.map((m) => <PublicMatch key={m.id} m={m} compact />)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PublicMatch({ m, showTime, compact }) {
  const isFinal = m.status === "final";
  return (
    <Card className="mb-2 p-3" data-testid={`public-match-${m.id}`}>
      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>{m.round_name}{m.field ? ` · ${m.field}` : ""}</span>
        {isFinal ? <span className="flex items-center gap-1 font-semibold text-emerald-600"><Lock className="h-3 w-3" /> FINAL</span>
          : m.status === "live" ? <LiveTag /> : (showTime || !compact) ? <span>{fmtTime(m.scheduled_time, m.timezone)}</span> : null}
      </div>
      <PSide m={m} side="a" win={isFinal && m.winner_id === (m.team_a && m.team_a.id)} />
      <PSide m={m} side="b" win={isFinal && m.winner_id === (m.team_b && m.team_b.id)} />
    </Card>
  );
}
function PSide({ m, side, win }) {
  const team = side === "a" ? m.team_a : m.team_b;
  const ph = side === "a" ? m.team_a_placeholder : m.team_b_placeholder;
  const score = side === "a" ? m.score_a : m.score_b;
  return (
    <div className={`flex items-center justify-between rounded px-1 py-0.5 ${win ? "bg-primary/10" : ""}`}>
      <div className="flex min-w-0 items-center gap-2">
        {team ? <TeamAvatar team={team} size={22} /> : <div className="h-[22px] w-[22px] rounded bg-muted" />}
        <span className={`truncate text-sm ${win ? "font-bold" : team ? "" : "text-muted-foreground"}`}>{team ? team.name : ph || "TBD"}</span>
      </div>
      {score != null && <span className="font-heading font-black">{score}</span>}
    </div>
  );
}

const SectionH = ({ children }) => <h3 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">{children}</h3>;
const Empty = ({ children }) => <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">{children}</p>;
